import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import Select from 'react-select';
import './userform.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import axiosInstance from '../../services/AxiosInstance';
import { FiArrowLeft } from 'react-icons/fi';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import { isBranchAdmin, isDepartmentAdmin, isSubDepartmentAdmin, getCurrentUserRole, isCorporateAdmin, ROLES } from '../../utils/roleAccess';
import { FIELD_LIMITS } from '../../utils/fieldLimits';

const AddUser = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    CorporateId: '',
    UserType: "user",
    Password: '',
    RoleId: '',
    FullName: '',
    Email: '',
    Department: '',
    EmployeeNumber: '',
    ContactNumber: '',
    CountryId: '',
    selectedMobileCode: '',
    BranchId: '',
    DepartmentId: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [roleList, setRoleList] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [countries, setCountries] = useState([]);
  const [selectedMobileCode, setSelectedMobileCode] = useState("");
  const [autoPassword, setAutoPassword] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [branchList, setBranchList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [subDepartmentList, setSubDepartmentList] = useState([]);
  const [isBranchDisabled, setIsBranchDisabled] = useState(false);
  const [isDepartmentDisabled, setIsDepartmentDisabled] = useState(false);
  const [isSubDepartmentDisabled, setIsSubDepartmentDisabled] = useState(false);
  const [currentUserRole, setCurrentUserRole] = useState(null);
  const [selectedRoleName, setSelectedRoleName] = useState(null);

  // Helper function to get role hierarchy level (lower number = higher privilege)
  const getRoleHierarchyLevel = (roleName) => {
    if (!roleName) return 999;
    const normalized = roleName.toLowerCase().replace(/\s+/g, '_').trim();
    const hierarchy = {
      'corporate_admin': 1,
      'admin': 1, // Legacy admin
      'branch_admin': 2,
      'department_admin': 3,
      'sub_department_admin': 4,
      'user': 5
    };
    return hierarchy[normalized] || 999;
  };

  // Filter roles based on current user's privilege
  // Corporate admin can create other corporate admins, other roles can only create lower privilege roles
  const getFilteredRoles = () => {
    if (!currentUserRole || !roleList.length) return roleList.filter(role => role.name !== 'super_admin');
    
    const currentUserLevel = getRoleHierarchyLevel(currentUserRole);
    const normalizedCurrentRole = currentUserRole.toLowerCase().replace(/\s+/g, '_').trim();
    
    return roleList.filter(role => {
      if (role.name === 'super_admin') return false;
      
      const roleLevel = getRoleHierarchyLevel(role.name);
      const normalizedRoleName = role.name.toLowerCase().replace(/\s+/g, '_').trim();
      
      // Corporate admin can create other corporate admins (same level)
      if (normalizedCurrentRole === 'corporate_admin' || normalizedCurrentRole === 'admin') {
        if (normalizedRoleName === 'corporate_admin' || normalizedRoleName === 'admin') {
          return true;
        }
      }
      
      // For all roles, can create roles with higher hierarchy level (lower privilege)
      return roleLevel > currentUserLevel;
    });
  };

  // Get role name from roleId
  const getRoleNameById = (roleId) => {
    const role = roleList.find(r => r.id === roleId);
    return role ? role.name : null;
  };

  // Check if field is required based on selected role
  const isBranchRequired = () => {
    if (!selectedRoleName) return false;
    const normalized = selectedRoleName.toLowerCase().replace(/\s+/g, '_').trim();
    return normalized === 'branch_admin' || normalized === 'department_admin' || normalized === 'sub_department_admin';
  };

  const isDepartmentRequired = () => {
    if (!selectedRoleName) return false;
    const normalized = selectedRoleName.toLowerCase().replace(/\s+/g, '_').trim();
    return normalized === 'department_admin' || normalized === 'sub_department_admin';
  };

  const isSubDepartmentRequired = () => {
    if (!selectedRoleName) return false;
    const normalized = selectedRoleName.toLowerCase().replace(/\s+/g, '_').trim();
    return normalized === 'sub_department_admin';
  };
  const notifyTopRight = (data) => {
    toast.success(`✔️ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
    });
  };
  const notifyError = (data) => {

    toast.error(`❌ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };
  const userData = localStorage.getItem('user');
  const parsedUser = userData ? JSON.parse(userData) : null;
  
  const fetchSubDepartments = async (departmentId = null, branchId = null) => {
    try {
      const userData = JSON.parse(localStorage.getItem('user'));
      const corporateId = userData?.corporateId || userData?.id;
      const buildUrl = (base) => {
        let url = `${base}?corporate_id=${corporateId}`;
        if (departmentId) url += `&department_id=${departmentId}`;
        if (branchId) url += `&branch_id=${branchId}`;
        return url;
      };
      // Try camel-case endpoint first
      let res;
      try {
        res = await axiosInstance.get(buildUrl(`/settings/subDepartments`));
      } catch (e1) {
        // Fallback to lowercase endpoint if the first fails
        res = await axiosInstance.get(buildUrl(`/settings/subdepartments`));
      }
      const data = res.data;
      setSubDepartmentList(data.data || data || []);
    } catch (err) {
      console.error('Sub-department list error:', err);
      setSubDepartmentList([]);
    }
  };

  useEffect(() => {

    const corporateId = parsedUser?.corporateId || '';

    setFormData((prev) => ({
      ...prev,
      CorporateId: corporateId,
      CreatedBy: corporateId
    }));

    // set country & phone code from logged-in user
    if (parsedUser?.country) {
      setFormData((prev) => ({
        ...prev,
        CountryName: parsedUser.country
      }));
    }

    const fetchRoles = async () => {
      try {
        const res = await axiosInstance.get(`/corporaterolepermission/corporateuserrolelist`);
        const data = res.data;
        if (res) {
          setRoleList(data.data || []);
        }
      } catch (err) {
        console.error('Role list error:', err);
      }
    };

    const fetchCountries = async () => {
      try {
        const res = await axiosInstance.get(`/auth/countrylist`);
        const data = res.data;
        if (Array.isArray(data)) {
          setCountries(data);
          data.map((country) => {
            if (country.name === parsedUser?.country) {
              setSelectedMobileCode(country.phone_code || country.CountryCode || country.code || "");
              setFormData((prev) => ({
                ...prev,
                CountryId: country.ID
              }));
            }
          });
        }
      } catch (err) {
        console.error("Failed to fetch countries:", err);
      }
    };

    const fetchBranches = async () => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        const corporateId = userData?.corporateId || userData?.id;
        const res = await axiosInstance.get(`/settings/branches?corporate_id=${corporateId}`);
        const data = res.data;
        if (res) {
          setBranchList(data.data || data || []);
        }
      } catch (err) {
        console.error('Branch list error:', err);
      }
    };

    const fetchDepartments = async (branchId = null) => {
      try {
        const userData = JSON.parse(localStorage.getItem('user'));
        const corporateId = userData?.corporateId || userData?.id;
        let url = `/settings/departments?corporate_id=${corporateId}`;
        if (branchId) {
          url += `&branch_id=${branchId}`;
        }
        const res = await axiosInstance.get(url);
        const data = res.data;
        if (res) {
          setDepartmentList(data.data || data || []);
        }
      } catch (err) {
        console.error('Department list error:', err);
      }
    };


    // Fetch current user role and details for role-based field restrictions
    const initializeRoleBasedFields = async () => {
      try {
        const role = getCurrentUserRole();
        setCurrentUserRole(role);
        
        if (isBranchAdmin(role) || isDepartmentAdmin(role) || isSubDepartmentAdmin(role)) {
          const userData = JSON.parse(localStorage.getItem('user'));
          const userId = userData?.id;
          
          if (userId) {
            try {
              const userDetailsRes = await axiosInstance.get(`/auth/details/${userId}`);
              const userDetails = userDetailsRes.data;
              
              const branchId = userDetails?.Branch?.id;
              const departmentId = userDetails?.Department?.id;
              const subDepartmentId = userDetails?.SubDepartment?.id;
              
              // Prefill and disable fields based on role
              if (isSubDepartmentAdmin(role)) {
                // Sub department admin: prefill and disable branch, department, sub_department
                if (branchId) {
                  setFormData((prev) => ({ ...prev, BranchId: branchId }));
                  setIsBranchDisabled(true);
                }
                if (departmentId) {
                  setFormData((prev) => ({ ...prev, DepartmentId: departmentId }));
                  setIsDepartmentDisabled(true);
                  // Fetch departments for the branch to populate the list
                  await fetchDepartments(branchId);
                }
                if (subDepartmentId) {
                  setFormData((prev) => ({ ...prev, SubDepartmentId: subDepartmentId }));
                  setIsSubDepartmentDisabled(true);
                  // Fetch sub-departments for the department
                  await fetchSubDepartments(departmentId, branchId);
                }
              } else if (isDepartmentAdmin(role)) {
                // Department admin: prefill and disable branch, department
                if (branchId) {
                  setFormData((prev) => ({ ...prev, BranchId: branchId }));
                  setIsBranchDisabled(true);
                }
                if (departmentId) {
                  setFormData((prev) => ({ ...prev, DepartmentId: departmentId }));
                  setIsDepartmentDisabled(true);
                  // Fetch departments for the branch to populate the list
                  await fetchDepartments(branchId);
                  // Fetch sub-departments for the department
                  await fetchSubDepartments(departmentId, branchId);
                }
              } else if (isBranchAdmin(role)) {
                // Branch admin: prefill and disable branch
                if (branchId) {
                  setFormData((prev) => ({ ...prev, BranchId: branchId }));
                  setIsBranchDisabled(true);
                  // Fetch departments for the branch
                  await fetchDepartments(branchId);
                }
              }
            } catch (err) {
              console.error('Error fetching current user details:', err);
            }
          }
        }
      } catch (err) {
        console.error('Error initializing role-based fields:', err);
      }
    };

    const initializeAll = async () => {
      await fetchCountries();
      await fetchRoles();
      await fetchBranches();
      await fetchDepartments();
      await initializeRoleBasedFields();
    };
    
    initializeAll();
  }, []);

  useEffect(() => {
    // Fetch all sub-departments on load
    fetchSubDepartments(null, null);
  }, []);

  // Initialize selectedRoleName when RoleId or roleList changes
  useEffect(() => {
    if (formData.RoleId && roleList.length > 0) {
      const roleName = getRoleNameById(formData.RoleId);
      if (roleName && !selectedRoleName) {
        setSelectedRoleName(roleName);
      }
    }
  }, [formData.RoleId, roleList]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
    
    // Update dial code when country changes
    if (name === 'CountryId') {
      const selected = countries.find((c) => String(c.id || c.ID) === String(value));
      if (selected) {
        const phoneCode = selected.phone_code || selected.CountryCode || selected.code || "";
        setSelectedMobileCode(phoneCode);
        setFormData((prev) => ({
          ...prev,
          selectedMobileCode: phoneCode
        }));
      }
    }
  };

  const handleBranchChange = (e) => {
    const { value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      BranchId: value,
      DepartmentId: '', // Reset department when branch changes
      SubDepartmentId: ''
    }));

    // Fetch departments for selected branch
    if (value) {
      const fetchDepartmentsForBranch = async () => {
        try {
          const userData = JSON.parse(localStorage.getItem('user'));
          const corporateId = userData?.corporateId || userData?.id;
          const res = await axiosInstance.get(`/settings/departments?corporate_id=${corporateId}&branch_id=${value}`);
          const data = res.data;
          if (res) {
            setDepartmentList(data.data || data || []);
          }
        } catch (err) {
          console.error('Department list error:', err);
        }
      };
      fetchDepartmentsForBranch();
      // Clear and refetch sub-departments for new branch
      setSubDepartmentList([]);
    } else {
      // If no branch selected, fetch all departments
      const fetchAllDepartments = async () => {
        try {
          const userData = JSON.parse(localStorage.getItem('user'));
          const corporateId = userData?.corporateId || userData?.id;
          const res = await axiosInstance.get(`/settings/departments?corporate_id=${corporateId}`);
          const data = res.data;
          if (res) {
            setDepartmentList(data.data || data || []);
          }
        } catch (err) {
          console.error('Department list error:', err);
        }
      };
      fetchAllDepartments();
      setSubDepartmentList([]);
    }
  };

  const handleDepartmentChange = (e) => {
    const { value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      DepartmentId: value,
      SubDepartmentId: ''
    }));

    fetchSubDepartments(value, formData.BranchId);
  };

  const generateRandomPassword = () => {
    const length = 10;
    const charset = 'abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*()_+';
    let password = '';
    for (let i = 0; i < length; i++) {
      password += charset.charAt(Math.floor(Math.random() * charset.length));
    }
    return password;
  };

  const handleAutoPasswordToggle = (e) => {
    const isChecked = e.target.checked;
    setAutoPassword(isChecked ? 1 : 0);
    setFormData((prev) => ({
      ...prev,
      Password: isChecked ? generateRandomPassword() : ''
    }));

  };

  const validate = () => {
    const newErrors = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!formData.FullName.trim()) newErrors.FullName = 'Full Name is required';
    else if (formData.FullName.length > FIELD_LIMITS.FULL_NAME) newErrors.FullName = `Full Name cannot exceed ${FIELD_LIMITS.FULL_NAME} characters`;
    if (!formData.Email.trim()) newErrors.Email = 'Email is required';
    else if (!emailRegex.test(formData.Email)) newErrors.Email = 'Email is invalid';

    if (formData.EmployeeNumber.trim() && formData.EmployeeNumber.length > FIELD_LIMITS.EMPLOYEE_NUMBER) {
      newErrors.EmployeeNumber = `Employee Number cannot exceed ${FIELD_LIMITS.EMPLOYEE_NUMBER} characters`;
    }
    if (formData.ContactNumber.trim()) {
      if (!/^\d+$/.test(formData.ContactNumber)) {
        newErrors.ContactNumber = 'Contact Number must be a valid number';
      } else if (formData.ContactNumber.length < 8 || formData.ContactNumber.length > 15) {
        newErrors.ContactNumber = 'Contact Number should be between 8 to 15 digits';
      }
    }
    if (!formData.Password.trim()) newErrors.Password = 'Password is required';
    else if (formData.Password.length < 6) newErrors.Password = 'Password must be at least 6 characters';
    if (!formData.RoleId) newErrors.RoleId = 'Role is required';

    // Validate fields based on selected role
    if (selectedRoleName) {
      const normalizedRole = selectedRoleName.toLowerCase().replace(/\s+/g, '_').trim();
      
      // Branch admin role requires branch
      if (normalizedRole === 'branch_admin' || normalizedRole === 'department_admin' || normalizedRole === 'sub_department_admin') {
        if (!formData.BranchId) {
          newErrors.BranchId = 'Branch is required for this role';
        }
      }
      
      // Department admin role requires department
      if (normalizedRole === 'department_admin' || normalizedRole === 'sub_department_admin') {
        if (!formData.DepartmentId) {
          newErrors.DepartmentId = 'Department is required for this role';
        }
      }
      
      // Sub department admin role requires sub department
      if (normalizedRole === 'sub_department_admin') {
        if (!formData.SubDepartmentId) {
          newErrors.SubDepartmentId = 'Sub Department is required for this role';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!validate()) return;

    // Additional validation: Check if selected role is allowed (prevent same/higher privilege)
    // Skip this validation for corporate admin
    const normalizedCurrentRole = currentUserRole ? currentUserRole.toLowerCase().replace(/\s+/g, '_').trim() : '';
    if (formData.RoleId && currentUserRole && normalizedCurrentRole !== 'corporate_admin' && normalizedCurrentRole !== 'admin') {
      const selectedRole = getRoleNameById(formData.RoleId);
      if (selectedRole) {
        const selectedRoleLevel = getRoleHierarchyLevel(selectedRole);
        const currentUserLevel = getRoleHierarchyLevel(currentUserRole);
        
        if (selectedRoleLevel <= currentUserLevel) {
          notifyError('You cannot create a user with the same or higher privilege level than yours.');
          setErrors({ RoleId: 'You cannot create a user with the same or higher privilege level.' });
          return;
        }
      }
    }

    setLoading(true);
    setIsSubmitting(true);

    const userData = JSON.parse(localStorage.getItem('user'));
    const selectedCountry = countries.find((c) => String(c.id || c.ID) === String(formData.CountryId));

    const payload = {
      ...formData,
      AutoGenerated: autoPassword,
      CountryId: formData.CountryId,
      CountryName: selectedCountry?.name || userData?.country || "",
      ContactNumber: formData.ContactNumber ? `${selectedMobileCode}${formData.ContactNumber}` : '',
      RoleId: formData.RoleId,
      SubDepartmentId: formData.SubDepartmentId || null
    };

    console.log('Final payload:', payload);

    try {
      const res = await axiosInstance.post(`/auth/corporateuser/add`, JSON.stringify(payload));

      const result = res;
      if (res) {
        notifyTopRight('✅ Corporate user registered successfully!');
        setTimeout(() => {
          setIsSubmitting(false);
          navigate('/settings/userlist');
        }, 2000);

        setFormData({
          CorporateId: '',
          UserType: 'user',
          RoleId: '',
          FullName: '',
          Email: '',
          Department: '',
          EmployeeNumber: '',
          ContactNumber: '',
          CountryId: '',
          selectedMobileCode: '',
          BranchId: '',
          DepartmentId: '',
          SubDepartmentId: ''
        });
      } else {
        notifyError(`❌ Error: ${result.message}`);
      }
    } catch (err) {
      console.error('Submit error:', err);
      if (err.response && err.response.status === 402) {
        notifyError('You don\'t have any active subscription. Please subscribe to a plan first.');
      } else if (err.response && err.response.status === 422) {
        notifyError('Maximum number of users allowed for your subscription plan has been reached. Please upgrade your plan to add more users.');
      } else if (err.response && err.response.status === 409) {
        notifyError('Email already exists');
      } else {
        notifyError(`❌ ${err.response?.data?.message || 'An error occurred.'}`);
      }
      setIsSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <button
        className="back-button"
        onClick={() => {
          if (window.history.length > 2) {
            navigate(-1);
          } else {
            navigate('/settings/userlist');
          }
        }}
      >
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>
      <div className="row" style={{ marginBottom: '0px !important' }}>
        <ToastContainer />
        <div className="col-xl-12 col-xxl-12">
          <div className="card">
            <div className="card-body">
              <section>
                <div className="row">
                  <div className="col-lg-6 mb-2">
                    <div className="form-group mb-3">
                      <label className="text-label">Full Name*</label>
                      <input
                        type="text"
                        name="FullName"
                        className="form-control"
                        placeholder="Full Name"
                        required
                        maxLength={FIELD_LIMITS.FULL_NAME}
                        value={formData.FullName}
                        onChange={handleChange}
                      />
                      {errors.FullName && <div className="text-danger">{errors.FullName}</div>}
                    </div>
                  </div>
                  <div className="col-lg-6 mb-2">
                    <div className="form-group mb-3">
                      <label className="text-label">Employee Number</label>
                      <input
                        type="text"
                        name="EmployeeNumber"
                        className="form-control"
                        placeholder="Employee Number"
                        maxLength={FIELD_LIMITS.EMPLOYEE_NUMBER}
                        value={formData.EmployeeNumber}
                        onChange={handleChange}
                      />
                      {errors.EmployeeNumber && <div className="text-danger">{errors.EmployeeNumber}</div>}
                    </div>
                  </div>

                  <div className="col-lg-6 mb-2">
                    <div className="form-group mb-3">
                      <label className="text-label">Email Address*</label>
                      <input
                        type="email"
                        name='Email'
                        className="form-control"
                        id="inputGroupPrepend2"
                        aria-describedby="inputGroupPrepend2"
                        placeholder="example@example.com"
                        value={formData.Email}
                        onChange={handleChange}
                        required
                      />
                      {errors.Email && <div className="text-danger">{errors.Email}</div>}
                    </div>
                  </div>

                  <div className="col-lg-6 mb-2">
                    <div className="form-group mb-3">
                      <label className="text-label">Contact Number</label>
                      <div className="input-group">
                        <div className="position-relative" style={{ width: '57px' }}>
                          <span
                            className="phonecode"
                            style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', paddingLeft: '0.75rem', pointerEvents: 'none' }}
                          >
                            {selectedMobileCode || 'Code'}
                          </span>
                          <select
                            name="CountryId"
                            className="form-select phonecode"
                            value={String(formData.CountryId || '')}
                            onChange={handleChange}
                            title="Select country code"
                            style={{ position: 'absolute', inset: 0, opacity: 0, zIndex: 2 }}
                          >
                            <option value="">Code</option>
                            {countries.map((country) => (
                              <option key={country.id || country.ID} value={String(country.id || country.ID)}>
                                {(country.phone_code || country.CountryCode || country.code || '') + ' ' + (country.name || '')}
                              </option>
                            ))}
                          </select>
                        </div>
                        <input
                          type="number"
                          name="ContactNumber"
                          className="form-control"
                          placeholder="Phone Number"
                          onChange={handleChange}
                          value={formData.ContactNumber}
                        />
                      </div>
                      {errors.ContactNumber && <div className="text-danger">{errors.ContactNumber}</div>}
                    </div>
                  </div>

                  <div className="col-lg-6 mb-3">
                    <div className="form-group mb-3">
                      <label className="user-form-label mb-1"><strong>Branch{isBranchRequired() ? '*' : ''}</strong></label>
                      <div className="user-form-select-container">
                        <Select
                          classNamePrefix="user-form-select"
                          placeholder="Select Branch"
                          isClearable
                          isDisabled={isBranchDisabled}
                          value={branchList.find((branch) => branch.id === formData.BranchId) 
                            ? { value: formData.BranchId, label: branchList.find(b => b.id === formData.BranchId)?.name }
                            : null
                          }
                          onChange={(selectedOption) => {
                            const value = selectedOption ? selectedOption.value : '';
                            setFormData((prevData) => ({
                              ...prevData,
                              BranchId: value,
                              DepartmentId: '',
                              SubDepartmentId: ''
                            }));
                            
                            if (value) {
                              const fetchDepartmentsForBranch = async () => {
                                try {
                                  const userData = JSON.parse(localStorage.getItem('user'));
                                  const corporateId = userData?.corporateId || userData?.id;
                                  const res = await axiosInstance.get(`/settings/departments?corporate_id=${corporateId}&branch_id=${value}`);
                                  const data = res.data;
                                  if (res) {
                                    setDepartmentList(data.data || data || []);
                                  }
                                } catch (err) {
                                  console.error('Department list error:', err);
                                }
                              };
                              fetchDepartmentsForBranch();
                              setSubDepartmentList([]);
                            } else {
                              const fetchAllDepartments = async () => {
                                try {
                                  const userData = JSON.parse(localStorage.getItem('user'));
                                  const corporateId = userData?.corporateId || userData?.id;
                                  const res = await axiosInstance.get(`/settings/departments?corporate_id=${corporateId}`);
                                  const data = res.data;
                                  if (res) {
                                    setDepartmentList(data.data || data || []);
                                  }
                                } catch (err) {
                                  console.error('Department list error:', err);
                                }
                              };
                              fetchAllDepartments();
                              setSubDepartmentList([]);
                            }
                          }}
                          options={branchList.map((branch) => ({
                            value: branch.id,
                            label: branch.name
                          }))}
                        />
                      </div>
                      {errors.BranchId && <p className='error'>{errors.BranchId}</p>}
                    </div>
                  </div>

                  <div className="col-lg-6 mb-3">
                    <div className="form-group mb-3">
                      <label className="user-form-label mb-1"><strong>Department{isDepartmentRequired() ? '*' : ''}</strong></label>
                      <div className="user-form-select-container">
                        <Select
                          classNamePrefix="user-form-select"
                          placeholder="Select Department"
                          isClearable
                          isDisabled={isDepartmentDisabled}
                          value={departmentList.find((dept) => dept.id === formData.DepartmentId) 
                            ? { 
                                value: formData.DepartmentId, 
                                label: `${departmentList.find(d => d.id === formData.DepartmentId)?.name} ${departmentList.find(d => d.id === formData.DepartmentId)?.branch_name ? `(${departmentList.find(d => d.id === formData.DepartmentId)?.branch_name})` : ''}`
                              }
                            : null
                          }
                          onChange={(selectedOption) => {
                            const value = selectedOption ? selectedOption.value : '';
                            setFormData((prevData) => ({
                              ...prevData,
                              DepartmentId: value,
                              SubDepartmentId: ''
                            }));
                            fetchSubDepartments(value, formData.BranchId);
                          }}
                          options={departmentList.map((department) => ({
                            value: department.id,
                            label: `${department.name} ${department.branch_name ? `(${department.branch_name})` : ''}`
                          }))}
                        />
                      </div>
                      {errors.DepartmentId && <p className='error'>{errors.DepartmentId}</p>}
                    </div>
                  </div>

                  <div className="col-lg-6 mb-3">
                    <div className="form-group mb-3">
                      <label className="user-form-label mb-1"><strong>Sub Department{isSubDepartmentRequired() ? '*' : ''}</strong></label>
                      <div className="user-form-select-container">
                        <Select
                          classNamePrefix="user-form-select"
                          placeholder="Select Sub Department"
                          isClearable
                          isDisabled={isSubDepartmentDisabled || !formData.DepartmentId}
                          value={subDepartmentList.find((sd) => sd.id === formData.SubDepartmentId) 
                            ? { 
                                value: formData.SubDepartmentId, 
                                label: `${subDepartmentList.find(s => s.id === formData.SubDepartmentId)?.name} ${subDepartmentList.find(s => s.id === formData.SubDepartmentId)?.department_name ? `(${subDepartmentList.find(s => s.id === formData.SubDepartmentId)?.department_name}, ${subDepartmentList.find(s => s.id === formData.SubDepartmentId)?.branch_name})` : ''}`
                              }
                            : null
                          }
                          onChange={(selectedOption) => {
                            setFormData((prevData) => ({
                              ...prevData,
                              SubDepartmentId: selectedOption ? selectedOption.value : ''
                            }));
                          }}
                          options={subDepartmentList.map((sd) => ({
                            value: sd.id,
                            label: `${sd.name} ${sd.department_name ? `(${sd.department_name}, ${sd.branch_name})` : ''}`
                          }))                          }
                        />
                      </div>
                      {errors.SubDepartmentId && <p className='error'>{errors.SubDepartmentId}</p>}
                    </div>
                  </div>

                  <div className="col-lg-6 mb-3">
                    <div className="form-group mb-3">
                      <label className="user-form-label mb-1"><strong>Role*</strong></label>
                      <div className="user-form-select-container">
                        <Select
                          classNamePrefix="user-form-select"
                          placeholder="Select Role"
                          isClearable
                          value={getFilteredRoles().find((role) => role.id === formData.RoleId) 
                              ? { 
                                  value: formData.RoleId, 
                                  label: roleList.find(r => r.id === formData.RoleId)?.name.charAt(0).toUpperCase() + 
                                         roleList.find(r => r.id === formData.RoleId)?.name.slice(1).replace(/_/g, ' ')
                                }
                              : null
                          }
                          onChange={(selectedOption) => {
                            const roleId = selectedOption ? selectedOption.value : '';
                            const roleName = selectedOption ? getRoleNameById(roleId) : null;
                            setFormData((prevData) => ({
                              ...prevData,
                              RoleId: roleId
                            }));
                            setSelectedRoleName(roleName);
                            // Clear errors when role changes
                            if (errors.BranchId || errors.DepartmentId || errors.SubDepartmentId) {
                              setErrors((prevErrors) => {
                                const newErrors = { ...prevErrors };
                                delete newErrors.BranchId;
                                delete newErrors.DepartmentId;
                                delete newErrors.SubDepartmentId;
                                return newErrors;
                              });
                            }
                          }}
                          options={getFilteredRoles().map((role) => ({
                            value: role.id,
                            label: role.name.charAt(0).toUpperCase() + role.name.slice(1).replace(/_/g, ' ')
                          }))}
                        />
                      </div>
                      {errors.RoleId && <p className='error'>{errors.RoleId}</p>}
                    </div>
                  </div>

                  <div className="col-lg-6 mb-3">
                    <div className="form-group mb-3">
                      <label className="user-form-label mb-1"><strong>Password</strong></label>
                      <div className="position-relative password-input-wrapper">
                        <input
                          type={showPassword ? "text" : "password"}
                          name='Password'
                          className="form-control user-form-input"
                          placeholder="password"
                          defaultValue={formData.Password}
                          onChange={handleChange}
                        />
                        <button
                          type="button"
                          className="password-toggle-btn"
                          onClick={() => setShowPassword(!showPassword)}
                        >
                          <i className={`fas ${showPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                        </button>
                      </div>
                      {errors.Password && <div className="user-form-error">{errors.Password}</div>}
                    </div>
                  </div>
                  <div className="text-end toolbar toolbar-bottom p-2">
                    <button 
                      type="button"
                      className="btn btn-cancel me-2" 
                      onClick={() => {
                        if (window.history.length > 2) {
                          navigate(-1);
                        } else {
                          navigate('/settings/userlist');
                        }
                      }}
                      disabled={isSubmitting}
                      style={{ maxWidth: '100px' }}
                    >
                      Cancel
                    </button>
                    <button className="btn user-form-btn sw-btn-next" onClick={handleSubmit} disabled={isSubmitting} style={{ maxWidth: '100px' }}>
                      {loading ? (
                        <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      ) : null}
                      Submit
                    </button>
                  </div>

                  <div className="col-lg-12 mt-2">
                    <p style={{ fontSize: '0.85rem', color: 'gray', marginBottom: '0.5rem' }}>
                      <strong>Role Permissions:</strong>
                    </p>
                    <ul style={{ fontSize: '0.85rem', color: 'gray', margin: 0 }}>
                      <li><strong>Corporate Admin:</strong> Will have access to all users, their settings, branches, and departments</li>
                      <li><strong>Branch Admin:</strong> Similar to Corporate Admin, but with access to the specific branch and their departments</li>
                      <li><strong>Department Admin:</strong> Will have access to users, their settings and all sub departments</li>
                      <li><strong>Sub Department Admin:</strong> Will have access to users, and their settings of the specific sub departments only</li>
                    </ul>
                  </div>

                </div>
              </section>
            </div>
          </div>
        </div>
        <InternalFooter />
      </div>
    </div>
  );
};

export default AddUser;
